import React from "react";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { Box, Theme, Typography, useMediaQuery, useTheme } from "@mui/material";
import { SymbolV2 } from "@rentablo/api/apiv1";
import { SearchModal } from "@components/Modal/SearchModal";
import SearchIcon from "../../../../icons/myicons/sidebar/search.svg?react";

const Container = styled("div", {
  shouldForwardProp: (prop) =>
    prop !== "collapsed" && prop !== "sideBar" && prop !== "bgColor",
})<{
  collapsed: number;
  sideBar: boolean;
  bgColor?: string;
}>(({ theme, collapsed, sideBar, bgColor }) => ({
  display: "flex",
  alignItems: "center",
  gap: 14,
  padding: theme.spacing(1.5, 3),
  borderRadius: theme.shape.borderRadius,
  cursor: "pointer",
  marginTop: collapsed ? theme.spacing(4) : 0,
  color: theme.palette.sidebar.searchText,
  backgroundColor: bgColor ?? theme.palette.sidebar.searchBg,
  border: `1px solid ${
    sideBar ? theme.palette.sidebar.searchBorder : theme.palette.border.primary
  }`,
  boxShadow: theme.type === "ftd" ? theme.shadows[1] : "none",
  [theme.breakpoints.down("sm")]: {
    marginTop: theme.spacing(4),
  },
}));

const SmallSearchButton = styled(Box)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: 38,
  cursor: "pointer",
  color: theme.palette.sidebar.searchText,
  "&:hover": {
    color: theme.palette.text.onColorDark,
    backgroundColor: theme.palette.sidebar.hover,
  },
}));

interface FakeSearchFieldProps {
  collapsed: boolean;
  placeholder?: string;
  bgColor?: string;
  sideBar?: boolean;
  onSymbolSelect?: (symbol: SymbolV2) => void;
}

export const FakeSearchField = (props: FakeSearchFieldProps) => {
  const {
    collapsed,
    bgColor,
    onSymbolSelect,
    sideBar = false,
    placeholder = "appSidebar.searchField",
  } = props;
  const theme = useTheme();

  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const matches = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!(event.target as HTMLElement).closest(".clear")) {
      setOpen(true);
    }
  };

  const handleSymbolSelect = (symbol: SymbolV2) => {
    setOpen(false);
    onSymbolSelect?.(symbol);
  };

  if (collapsed && !matches) {
    return (
      <>
        <SmallSearchButton onClick={handleClick}>
          <SearchIcon />
        </SmallSearchButton>
        <SearchModal
          isOpen={open}
          onClose={() => setOpen(false)}
          onSymbolSelect={handleSymbolSelect}
        />
      </>
    );
  }

  return (
    <>
      <Container
        onClick={handleClick}
        collapsed={collapsed ? 1 : 0}
        bgColor={bgColor}
        sideBar={sideBar}
      >
        <SearchIcon color={theme.palette.sidebar.searchText} />
        <Typography
          sx={{
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            overflow: "hidden",
          }}
          variant="body3"
          color={
            sideBar
              ? theme.palette.sidebar.searchText
              : theme.palette.text.tertiary
          }
        >
          {t(placeholder)}
        </Typography>
      </Container>
      {open && (
        <SearchModal
          isOpen={open}
          onClose={() => setOpen(false)}
          onSymbolSelect={handleSymbolSelect}
        />
      )}
    </>
  );
};
